import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  RefreshCw, CheckCircle, XCircle, AlertCircle, Send,
  User, CreditCard, Calendar, Syringe, Bell, Zap
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Membership {
  id: string;
  member_number: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string | null;
  pipeline_stage: string | null;
  location_id: string | null;
  visits_count: number;
  locations?: {
    name: string;
    city: string;
    state: string;
  } | null;
}

const GHL_STAGES = [
  { 
    id: "lead", 
    name: "Lead", 
    tag: "TrueTox-Lead",
    icon: User,
    description: "New lead captured from website"
  },
  { 
    id: "tox_pass_activated", 
    name: "Tox Pass Activated", 
    tag: "TrueTox-ToxPass-Activated",
    icon: CreditCard,
    description: "Member signed up for Tox Pass"
  },
  { 
    id: "consultation_booked", 
    name: "Consultation Booked", 
    tag: "TrueTox-Consultation-Booked",
    icon: Calendar,
    description: "Consultation appointment scheduled"
  },
  { 
    id: "payment_processed", 
    name: "Payment Processed", 
    tag: "TrueTox-Payment-Processed",
    icon: CreditCard,
    description: "Payment completed successfully"
  },
  { 
    id: "first_treatment_complete", 
    name: "First Treatment Complete", 
    tag: "TrueTox-First-Treatment",
    icon: Syringe,
    description: "First treatment administered"
  },
  { 
    id: "next_appointment_booked", 
    name: "Next Appointment Booked", 
    tag: "TrueTox-Next-Appointment",
    icon: Bell,
    description: "Follow-up appointment scheduled"
  },
];

export function GHLNotifications() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [resyncingId, setResyncingId] = useState<string | null>(null);
  const [selectedStage, setSelectedStage] = useState<string>("all");

  // Fetch email settings to show enabled/disabled status
  const { data: emailSettings } = useQuery({
    queryKey: ['email-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('email_settings')
        .select('*')
        .single();
      
      if (error) throw error;
      return data;
    },
  });

  // Fetch memberships for resync capability
  const { data: memberships = [], isLoading: membershipsLoading } = useQuery({
    queryKey: ['admin-memberships-ghl'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('memberships')
        .select(`
          id,
          member_number,
          customer_name,
          customer_email,
          customer_phone,
          pipeline_stage,
          location_id,
          visits_count,
          locations:location_id (
            name,
            city,
            state
          )
        `)
        .order('created_at', { ascending: false })
        .limit(50);
      
      if (error) throw error;
      return data as Membership[];
    },
  });

  const isStageEnabled = (stageId: string) => {
    if (!emailSettings) return true;
    const keyMap: Record<string, string> = {
      lead: 'stage_lead_enabled',
      tox_pass_activated: 'stage_tox_pass_activated_enabled',
      consultation_booked: 'stage_consultation_booked_enabled',
      payment_processed: 'stage_payment_processed_enabled',
      first_treatment_complete: 'stage_first_treatment_enabled',
      next_appointment_booked: 'stage_next_appointment_enabled',
    };
    return emailSettings[keyMap[stageId]] ?? true;
  };

  const resyncToGHL = async (membership: Membership) => {
    if (!membership.location_id) {
      toast({
        title: "Cannot Resync",
        description: "This member has no associated location.",
        variant: "destructive",
      });
      return;
    }

    setResyncingId(membership.id);

    try {
      const { data, error } = await supabase.functions.invoke('sync-ghl-contact', {
        body: {
          name: membership.customer_name,
          email: membership.customer_email,
          phone: membership.customer_phone,
          location_id: membership.location_id,
          source: `Manual Resync - Pipeline: ${membership.pipeline_stage || 'lead'}`,
          pipeline_stage: membership.pipeline_stage || 'lead',
          member_number: membership.member_number,
          visits_count: membership.visits_count,
        },
      });

      if (error) throw error;

      toast({
        title: "GHL Sync Successful",
        description: `${membership.customer_name} synced to GHL. Action: ${data?.action || 'updated'}`,
      });

      queryClient.invalidateQueries({ queryKey: ['admin-memberships-ghl'] });
    } catch (error: any) {
      console.error('GHL resync error:', error);
      toast({
        title: "Sync Failed",
        description: error.message || "Failed to sync contact to GHL",
        variant: "destructive",
      });
    } finally {
      setResyncingId(null);
    }
  };

  const resyncAllByStage = async (stageId: string) => {
    const membersToSync = memberships.filter(m => 
      m.pipeline_stage === stageId && m.location_id
    );

    if (membersToSync.length === 0) {
      toast({
        title: "No Members",
        description: `No members found in "${stageId}" stage with a location.`,
      });
      return;
    }

    toast({
      title: "Batch Sync Started",
      description: `Syncing ${membersToSync.length} members to GHL...`,
    });

    let successCount = 0;
    let failCount = 0;

    for (const member of membersToSync) {
      try {
        await supabase.functions.invoke('sync-ghl-contact', {
          body: {
            name: member.customer_name,
            email: member.customer_email,
            phone: member.customer_phone,
            location_id: member.location_id,
            source: `Batch Resync - Pipeline: ${member.pipeline_stage}`,
            pipeline_stage: member.pipeline_stage,
            member_number: member.member_number,
            visits_count: member.visits_count,
          },
        });
        successCount++;
      } catch {
        failCount++;
      }
    }

    toast({
      title: "Batch Sync Complete",
      description: `${successCount} synced, ${failCount} failed.`,
    });
  };

  const filteredMemberships = selectedStage === "all" 
    ? memberships 
    : memberships.filter(m => m.pipeline_stage === selectedStage);

  const getStageBadge = (stage: string | null) => {
    const stageInfo = GHL_STAGES.find(s => s.id === stage);
    if (!stageInfo) {
      return <Badge variant="outline">Unknown</Badge>;
    }
    const Icon = stageInfo.icon;
    return (
      <Badge variant="secondary" className="flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {stageInfo.name}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* GHL Notification Triggers */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" />
            GHL Notification Triggers
          </CardTitle>
          <CardDescription>
            Pipeline stages that trigger notifications to GoHighLevel. Tags are applied to contacts for workflow automation.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {GHL_STAGES.map((stage) => {
              const Icon = stage.icon;
              const enabled = isStageEnabled(stage.id);
              return (
                <div
                  key={stage.id}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    enabled 
                      ? 'border-primary/30 bg-primary/5' 
                      : 'border-muted bg-muted/30 opacity-60'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`p-2 rounded-lg ${enabled ? 'bg-primary/20' : 'bg-muted'}`}>
                        <Icon className={`w-4 h-4 ${enabled ? 'text-primary' : 'text-muted-foreground'}`} />
                      </div>
                      <span className="font-medium">{stage.name}</span>
                    </div>
                    {enabled ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-muted-foreground" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">{stage.description}</p>
                  <div className="flex items-center gap-2">
                    <code className="text-[10px] bg-background px-2 py-1 rounded border">
                      {stage.tag}
                    </code>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            Enable/disable stages in the Email Settings section above. When enabled, contacts are synced to GHL with the corresponding tag.
          </p>
        </CardContent>
      </Card>

      {/* Resync Members */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-primary" />
            Manual GHL Resync
          </CardTitle>
          <CardDescription>
            Resync member contacts to GoHighLevel if sync failed or data was updated.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1">
              <Select value={selectedStage} onValueChange={setSelectedStage}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by stage" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stages</SelectItem>
                  {GHL_STAGES.map((stage) => (
                    <SelectItem key={stage.id} value={stage.id}>
                      {stage.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedStage !== "all" && (
              <Button 
                variant="outline" 
                onClick={() => resyncAllByStage(selectedStage)}
                className="flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                Resync All in Stage
              </Button>
            )}
          </div>

          {membershipsLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading members...</div>
          ) : filteredMemberships.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="w-8 h-8 mx-auto mb-2" />
              No members found
            </div>
          ) : (
            <div className="rounded-md border overflow-auto max-h-96">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Member</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMemberships.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{member.customer_name}</p>
                          <p className="text-xs text-muted-foreground">{member.customer_email}</p>
                          <code className="text-[10px] text-primary">{member.member_number}</code>
                        </div>
                      </TableCell>
                      <TableCell>
                        {member.locations ? (
                          <span className="text-sm">
                            {member.locations.name}
                            <br />
                            <span className="text-xs text-muted-foreground">
                              {member.locations.city}, {member.locations.state}
                            </span>
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">No location</span>
                        )}
                      </TableCell>
                      <TableCell>{getStageBadge(member.pipeline_stage)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => resyncToGHL(member)}
                          disabled={resyncingId === member.id || !member.location_id}
                        >
                          {resyncingId === member.id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <RefreshCw className="w-4 h-4 mr-1" />
                              Resync
                            </>
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
